import { useCallback, useEffect, useState } from 'react';

function storageKey(userId) {
  return `sidebar_pinned_nav_${userId}`;
}

function normalizePaths(paths) {
  if (!Array.isArray(paths)) return [];
  const seen = new Set();
  const out = [];
  for (const p of paths) {
    if (typeof p !== 'string') continue;
    const path = p.trim();
    if (!path || seen.has(path)) continue;
    seen.add(path);
    out.push(path);
  }
  return out;
}

function readPins(userId) {
  if (!userId) return [];
  try {
    return normalizePaths(JSON.parse(localStorage.getItem(storageKey(userId)) || '[]'));
  } catch {
    return [];
  }
}

function writePins(userId, paths) {
  if (!userId) return;
  localStorage.setItem(storageKey(userId), JSON.stringify(paths));
}

/**
 * Per-user pinned sidebar paths (localStorage).
 * @param {string|number|null|undefined} userId
 */
export function usePinnedNav(userId) {
  const [pinnedPaths, setPinnedPathsState] = useState(() => readPins(userId));

  useEffect(() => {
    setPinnedPathsState(readPins(userId));
  }, [userId]);

  const setPinnedPaths = useCallback((next) => {
    const normalized = normalizePaths(typeof next === 'function' ? next(readPins(userId)) : next);
    setPinnedPathsState(normalized);
    writePins(userId, normalized);
  }, [userId]);

  const isPinned = useCallback((path) => pinnedPaths.includes(path), [pinnedPaths]);

  const togglePin = useCallback((path) => {
    if (!path || typeof path !== 'string') return;
    setPinnedPathsState((prev) => {
      const next = prev.includes(path)
        ? prev.filter((p) => p !== path)
        : normalizePaths([...prev, path]);
      writePins(userId, next);
      return next;
    });
  }, [userId]);

  return { pinnedPaths, isPinned, togglePin, setPinnedPaths };
}
